/**
 * Curated design-token whitelist for the Style panel — colors/fonts/
 * spacing/radius/shadows are all PICKED from this fixed list, never
 * free-text CSS. This is the client-side UX layer only; the actual
 * security boundary is backend/utils/landingPageSchema.js's
 * UNSAFE_STYLE_PATTERN scan + primitive-type restriction, which runs
 * regardless of what the UI offers.
 *
 * Colors reference the SAME CSS custom properties already defined on
 * :root in frontend/src/index.css — that stylesheet loads for the whole
 * SPA (including the guest-facing captive portal route), so
 * `var(--violet-600)` resolves correctly everywhere, keeping builder-made
 * pages visually consistent with the rest of the product's theme.
 *
 * Fonts are limited to 'Inter' (already loaded via Google Fonts in
 * public/index.html — the app's default everywhere), Marcellus and Jost
 * (bundled with the app through @fontsource, so a guest who is not online
 * yet still gets them — see Portal/LandingPageRenderer.jsx) plus universally
 * available system fonts that need no extra loading — no arbitrary/remote
 * font URLs, closing that injection/privacy surface entirely.
 *
 * The brand tokens read the company's own colours (Tenant Profile / Branches
 * → Branding), which the guest page sets as --lp-brand / --lp-accent; the
 * colour after the comma is used when the company has not chosen one. New
 * values here avoid quotes: a saved page passes through the global XSS
 * filter, which turns a quote into &#x27;.
 */

// Device size classes, widest to narrowest. Each one is a style bucket on a
// node and becomes a REAL CSS media query on the guest page, layered like
// ordinary CSS: `desktop` is the base, `wide` adds to it on big screens, and
// tablet -> mobile -> small each override the one before as the screen
// narrows (a phone at 375px gets desktop + tablet + mobile rules).
export const BREAKPOINTS = ['wide', 'desktop', 'tablet', 'mobile', 'small'];

export const COLOR_TOKENS = [
    { label: 'White', value: '#ffffff' },
    { label: 'Black', value: '#0f172a' },
    { label: 'Primary (Violet)', value: 'var(--violet-600)' },
    { label: 'Primary Light', value: 'var(--violet-50)' },
    { label: 'Pink', value: 'var(--pink-500)' },
    { label: 'Cyan', value: 'var(--cyan-500)' },
    { label: 'Success', value: 'var(--success-color)' },
    { label: 'Warning', value: 'var(--warning-color)' },
    { label: 'Danger', value: 'var(--danger-color)' },
    { label: 'Text Primary', value: 'var(--gray-900)' },
    { label: 'Text Secondary', value: 'var(--gray-500)' },
    { label: 'Border Gray', value: 'var(--gray-200)' },
    { label: 'Surface Gray', value: 'var(--gray-50)' },
    // Hotel-style accents (a deep teal panel with gold details)
    { label: 'Deep Teal', value: '#10505a' },
    { label: 'Deep Teal (see-through)', value: 'rgba(16, 80, 90, 0.92)' },
    { label: 'Gold', value: '#c4a05f' },
    { label: 'Soft Gold', value: '#d9c08a' },
    // Company branding — the colours its logo was uploaded with, else these defaults.
    { label: 'Brand colour (company branding)', value: 'var(--lp-brand, #221e5f)' },
    { label: 'Brand accent (company branding)', value: 'var(--lp-accent, #29a9e1)' },
    { label: 'Slate', value: '#5b6478' },
    // Hill-station green (the Scenic sign-in page's default)
    { label: 'Forest Green', value: '#1d3a2b' },
    { label: 'Transparent', value: 'transparent' },
];

export const GRADIENT_TOKENS = [
    { label: 'None', value: '' },
    { label: 'Brand (Violet → Pink)', value: 'var(--gradient-brand)' },
    { label: 'Violet', value: 'var(--gradient-violet)' },
    { label: 'Pink', value: 'var(--gradient-pink)' },
    { label: 'Cyan', value: 'var(--gradient-cyan)' },
];

export const FONT_TOKENS = [
    { label: 'Inter (default)', value: "'Inter', system-ui, sans-serif" },
    { label: 'Arial', value: 'Arial, Helvetica, sans-serif' },
    { label: 'Georgia', value: "Georgia, 'Times New Roman', serif" },
    { label: 'Verdana', value: 'Verdana, Geneva, sans-serif' },
    { label: 'Courier New', value: "'Courier New', Courier, monospace" },
    { label: 'Marcellus (elegant serif)', value: 'Marcellus, Georgia, serif' },
    { label: 'Jost (clean sans)', value: 'Jost, system-ui, sans-serif' },
];

export const FONT_SIZE_TOKENS = [
    { label: 'XS (12px)', value: '12px' },
    { label: 'Small (14px)', value: '14px' },
    { label: 'Base (16px)', value: '16px' },
    { label: 'Large (20px)', value: '20px' },
    { label: 'XL (24px)', value: '24px' },
    { label: '2XL (32px)', value: '32px' },
    { label: '3XL (40px)', value: '40px' },
];

export const FONT_WEIGHT_TOKENS = [
    { label: 'Normal', value: '400' },
    { label: 'Medium', value: '500' },
    { label: 'Semibold', value: '600' },
    { label: 'Bold', value: '700' },
];

export const SPACING_TOKENS = [
    { label: 'None', value: '0' },
    { label: 'XS (4px)', value: '4px' },
    { label: 'Small (8px)', value: '8px' },
    { label: 'Medium (16px)', value: '16px' },
    { label: 'Large (24px)', value: '24px' },
    { label: 'XL (32px)', value: '32px' },
    { label: '2XL (48px)', value: '48px' },
];

export const RADIUS_TOKENS = [
    { label: 'None', value: '0' },
    { label: 'Small (4px)', value: '4px' },
    { label: 'Medium (8px)', value: '8px' },
    { label: 'Large (16px)', value: '16px' },
    { label: 'Pill', value: '9999px' },
];

export const SHADOW_TOKENS = [
    { label: 'None', value: 'none' },
    { label: 'Small', value: 'var(--shadow-sm)' },
    { label: 'Medium', value: 'var(--shadow)' },
    { label: 'Large', value: 'var(--shadow-lg)' },
    { label: 'Brand Glow', value: 'var(--shadow-brand)' },
];

export const ALIGN_TOKENS = ['left', 'center', 'right'];

// Media query thresholds for the guest-facing renderer's generated CSS
// (landingPageStyles.js). Keep in step with nodeStyleSchema in
// backend/utils/landingPageSchema.js, which allows exactly these buckets.
export const WIDE_MIN_WIDTH = 1400;
export const BREAKPOINT_MAX_WIDTH = { tablet: 991, mobile: 639, small: 420 };

// Below this width free-placed layouts stack themselves (see buildResponsiveCss) —
// no per-device style needed. Matches the classic portal page's own breakpoint.
export const AUTO_STACK_MAX_WIDTH = 860;

export const BREAKPOINT_QUERIES = {
    wide: `(min-width: ${WIDE_MIN_WIDTH}px)`,
    tablet: `(max-width: ${BREAKPOINT_MAX_WIDTH.tablet}px)`,
    mobile: `(max-width: ${BREAKPOINT_MAX_WIDTH.mobile}px)`,
    small: `(max-width: ${BREAKPOINT_MAX_WIDTH.small}px)`,
};

// Toolbar labels for the size classes.
export const BREAKPOINT_LABELS = {
    wide: 'Wide screen', desktop: 'Desktop', tablet: 'Tablet', mobile: 'Mobile', small: 'Small phone',
};

/**
 * Which size class a page width falls into — the same thresholds the guest
 * page's real @media queries use, so resizing the builder's page frame by
 * mouse edits exactly what a device of that width would show.
 */
export function breakpointForWidth(px) {
    if (px <= BREAKPOINT_MAX_WIDTH.small) return 'small';
    if (px <= BREAKPOINT_MAX_WIDTH.mobile) return 'mobile';
    if (px <= BREAKPOINT_MAX_WIDTH.tablet) return 'tablet';
    if (px >= WIDE_MIN_WIDTH) return 'wide';
    return 'desktop';
}

export const BREAKPOINT_ICONS = {
    wide: 'fa-tv', desktop: 'fa-desktop', tablet: 'fa-tablet-alt', mobile: 'fa-mobile-alt', small: 'fa-mobile',
};

// Popular real devices, for the toolbar's device picker (CSS px widths).
export const DEVICE_PRESETS = [
    { label: 'Small phone', width: 320 },
    { label: 'iPhone SE', width: 375 },
    { label: 'iPhone 14', width: 390 },
    { label: 'Large phone', width: 430 },
    { label: 'iPad mini', width: 768 },
    { label: 'iPad Pro', width: 1024 },
    { label: 'Laptop', width: 1366 },
    { label: 'Desktop HD', width: 1920 },
];
